import { db, storage } from "./firebase";
import { collection, addDoc, onSnapshot, deleteDoc, doc, query, where, serverTimestamp, getDoc, updateDoc, getDocs, writeBatch } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";

export const addProject = (user, projectData) => {
    const projectsRef = collection(db, 'projects');

    let materialOwnerId = user.uid;
    if (user.role === 'collaborator' && user.createdBy) {
        materialOwnerId = user.createdBy;
    }

    return addDoc(projectsRef, {
        ...projectData,
        userId: user.uid,
        materialOwnerId, // Storing the effective owner ID
        createdAt: serverTimestamp()
    });
};

export const updateProjectPermissions = async (projectId, targetUserId, permissions) => {
    const projectDocRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectDocRef);
    if (!projectSnap.exists()) {
        throw new Error("Project not found");
    }

    const projectData = projectSnap.data();
    const currentPermissions = projectData.permissions || {};
    const currentAuthorizedUsers = projectData.authorizedUsers || [];

    // Update permissions for the target user
    currentPermissions[targetUserId] = permissions;

    // Determine if the user should be in the authorizedUsers array
    const hasAnyPermission = Object.values(permissions).some(p => p === true);

    let updatedAuthorizedUsers = [...currentAuthorizedUsers];

    if (hasAnyPermission && !currentAuthorizedUsers.includes(targetUserId)) {
        // Add user if they have at least one permission and are not already in the array
        updatedAuthorizedUsers.push(targetUserId);
    } else if (!hasAnyPermission && currentAuthorizedUsers.includes(targetUserId)) {
        // Remove user if they have no permissions and are in the array
        updatedAuthorizedUsers = currentAuthorizedUsers.filter(uid => uid !== targetUserId);
    }

    return updateDoc(projectDocRef, {
        permissions: currentPermissions,
        authorizedUsers: updatedAuthorizedUsers
    });
};

export const updateProjectDetails = (projectId, details) => {
    const projectDocRef = doc(db, 'projects', projectId);
    return updateDoc(projectDocRef, details);
};

export const onProjectsChange = (userId, callback) => {
    const projectsRef = collection(db, 'projects');
    
    // Query for projects owned by the user
    const ownedQuery = query(projectsRef, where("userId", "==", userId));
    
    // Query for projects shared with the user
    const sharedQuery = query(projectsRef, where("authorizedUsers", "array-contains", userId));

    let ownedProjects = [];
    let sharedProjects = [];

    const combineAndCallback = () => {
        const allProjects = [...ownedProjects, ...sharedProjects];
        const uniqueProjects = Array.from(new Map(allProjects.map(p => [p.id, p])).values());
        
        const sortedProjects = uniqueProjects.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : 0;
            return dateB - dateA;
        });

        callback(sortedProjects);
    };

    const unsubOwned = onSnapshot(ownedQuery, (snapshot) => {
        ownedProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        combineAndCallback();
    });

    const unsubShared = onSnapshot(sharedQuery, (snapshot) => {
        sharedProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        combineAndCallback();
    });
    
    return () => {
        unsubOwned();
        unsubShared();
    };
};

export const onProjectChange = (projectId, callback) => {
    const projectDocRef = doc(db, 'projects', projectId);
    return onSnapshot(projectDocRef, (doc) => {
        if (doc.exists()) {
            callback({ id: doc.id, ...doc.data() });
        } else {
            callback(null);
        }
    });
};

export const deleteProject = async (projectId) => {
    const projectDocRef = doc(db, 'projects', projectId);

    const safeDeleteFromStorage = async (url) => {
        // Only delete if it's a firebase storage URL.
        if (url && url.startsWith('https://firebasestorage.googleapis.com')) {
            try {
                const fileRef = ref(storage, url);
                await deleteObject(fileRef);
            } catch (error) {
                // It's okay if the object is not found, it might have been deleted already.
                if (error.code !== 'storage/object-not-found') {
                    console.warn(`Failed to delete storage object at ${url}:`, error);
                }
            }
        }
    };

    // 1. Delete all nodes in the 'nodes' subcollection and their associated storage files
    const nodesRef = collection(db, 'projects', projectId, 'nodes');
    const nodesSnapshot = await getDocs(nodesRef);
    if (!nodesSnapshot.empty) {
        const batch = writeBatch(db);
        for (const nodeDoc of nodesSnapshot.docs) {
            const nodeData = nodeDoc.data();
            // Delete associated file from storage
            if (nodeData.type === 'file' && nodeData.url) {
                await safeDeleteFromStorage(nodeData.url);
            }
            batch.delete(nodeDoc.ref);
        }
        await batch.commit();
    }

    // 2. Delete all messages in the 'messages' subcollection and their attachments
    const messagesRef = collection(db, 'projects', projectId, 'messages');
    const messagesSnapshot = await getDocs(messagesRef);
    if (!messagesSnapshot.empty) {
        const batch = writeBatch(db);
        for (const messageDoc of messagesSnapshot.docs) {
            const messageData = messageDoc.data();
            // Delete associated attachment from storage
            if (messageData.imageUrl) {
                await safeDeleteFromStorage(messageData.imageUrl);
            }
            batch.delete(messageDoc.ref);
        }
        await batch.commit();
    }
    
    // 3. Delete any images that were uploaded directly to the moodboard for this project
    const projectSnap = await getDoc(projectDocRef);
    if (projectSnap.exists()) {
        const projectData = projectSnap.data();
        if (projectData.moodboardState) {
            const allImageEntities = Object.values(projectData.moodboardState).flat().filter(e => e.type === 'image' && e.imageUrl);
            
            const projectStoragePath = `/projects%2F${projectId}%2F`;
            const urlsToDelete = allImageEntities
                .map(e => e.imageUrl)
                .filter(url => url && url.includes(projectStoragePath));
            
            const uniqueUrls = [...new Set(urlsToDelete)];

            for (const url of uniqueUrls) {
                await safeDeleteFromStorage(url);
            }
        }
    }

    // 4. Finally, delete the main project document
    return deleteDoc(projectDocRef);
};
